import { useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, ExternalLink, MapPin, Trophy } from "lucide-react";

import { LandingHeader, LandingFooter } from "@/components/landing/shared";
import CanonicalTags from "@/components/seo/CanonicalTags";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import OfficesCard from "@/components/organization/OfficesCard";
import { officesDisplayFor, publicWorkModelView } from "@/lib/workModel";
import WorkModelBadge from "@/components/organization/WorkModelBadge";
import { feeTypeLabels } from "@/lib/feeTypeLabels";
import RichTextRenderer from "@/components/ui/rich-text-renderer";
import { SectionHead, TagRow } from "@/components/organization/kit";
import OnboardingPlan, { ONBOARDING_PLAN_ANCHOR_ID } from "@/components/organization/value/OnboardingPlan";
import OnboardingPlanSummary from "@/components/organization/value/OnboardingPlanSummary";
import { normalizeOnboardingStages } from "@/lib/onboardingPlan";
import { Skeleton } from "@/components/ui/skeleton";
import { usePrerenderData } from "@/lib/prerenderData";
import { fetchPublicOrganizationProfile } from "@/lib/publicOrganizationRpc";
import { trackPublicProfileView } from "@/lib/analytics";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import LookingForCard from "@/components/organization/LookingForCard";
import PublicOrgStory from "@/components/org-story/PublicOrgStory";
import PublicOrgHeader from "@/components/org-story/PublicOrgHeader";
import PublicOrgFooter from "@/components/org-story/PublicOrgFooter";
import { StandalonePage, StandalonePanel } from "@/components/marketing/StandalonePage";
import { PublicSignInProvider } from "@/components/org-story/publicSignIn";
import PublicLoadError from "@/components/org-story/PublicLoadError";
import SignUpPrompt from "@/components/auth/SignUpPrompt";
import { GATED_INSIGHTS_LABEL } from "@/components/auth/signUpPromptCopy";
import OperationsSpecSheet from "@/components/org-story/operations/OperationsSpecSheet";
import { buildToolStack } from "@/components/org-story/storyData";
import { normalizeSupport } from "@/lib/orgSupport";
import { fromPublicProfile } from "@/lib/orgStory/fromPublicProfile";
import {
  articleDate,
  articleExcerpt,
  articleReadTime,
  contentTypeLabel,
  formatCompactCurrency,
  formatInteger,
  formatLongDate,
  formatPercent,
  httpUrl,
  locationLabel,
  lookingForFromPublic,
  organizationArticlePath,
  organizationJsonLd,
  organizationPath,
  orgTypeLabel,
  profileDescription,
  profileTitle,
  serializeJsonLd,
  type PublicOrganizationProfile as Profile,
} from "@/lib/publicOrganizationProfile";
import { useRetiredSlugRedirect } from "@/hooks/useRetiredSlugRedirect";
import ListedOrganizationView from "@/components/organization/listed/ListedOrganizationView";
import { isListedPublicProfile, listedIdentityFromPublic } from "@/lib/listedOrganization";
import { listingFactsFromPublic } from "@/lib/listingFacts";
import { cultureTags } from "@/lib/orgProfileOptions";

/**
 * The PUBLIC organization profile — what an anonymous visitor, and every
 * crawler, sees at /organization/:slug.
 *
 * It renders only what get_public_organization_profile returns (an explicit,
 * server-side allowlist — see migration 20260913120000) and is deliberately
 * lean: no AgentLayout, no auth-gated providers, no Mapbox or Recharts, so it
 * renders under Node in scripts/prerender.mjs and ships as static HTML for the
 * crawlers that do not execute JavaScript. Signed-in users never land here —
 * src/App.tsx routes them to the full OrganizationProfile at the same URL.
 *
 * Reviews, the team roster and social links are absent on purpose: each is
 * gated harder than "any authenticated user" by an earlier decision, and
 * making them public is a separate founder call recorded in the design spec.
 */

const PageShell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-background">
    <LandingHeader variant="main" />
    {children}
    <LandingFooter />
  </div>
);

const NotFound = () => (
  <StandalonePage>
    <Helmet>
      <title>Organization not found | Compaire</title>
      <meta name="robots" content="noindex" />
    </Helmet>
    <StandalonePanel
      tone="notice"
      title="Organization not found"
      actions={
        <Link to="/teams-brokerages" className="mk-btn mk-btn--outline">
          Explore teams and brokerages
        </Link>
      }
    >
      <p className="mk-body">This organization profile doesn't exist or hasn't been published yet.</p>
    </StandalonePanel>
  </StandalonePage>
);

/**
 * `bright` mirrors the flag-on body's theming so the loading skeleton doesn't
 * flash the legacy cream layout before the story mounts, and so it doesn't
 * leave a cream band beneath a short skeleton either — it uses the SAME
 * flex-column shell as the flag-on story body (not `PageShell`, which has no
 * `flex-1`). When `bright` is false (flag off), the output is byte-for-byte
 * the pre-existing skeleton.
 */
const skeletons = (
  <>
    <Skeleton className="aspect-[3/1] w-full rounded-2xl" />
    <Skeleton className="h-10 w-2/3" />
    <Skeleton className="h-5 w-1/2" />
    <Skeleton className="h-40 w-full" />
  </>
);

const Loading = ({ bright = false }: { bright?: boolean }) => {
  if (bright) {
    return (
      <PublicSignInProvider>
        <div className="mk-app flex min-h-screen flex-col">
          {/* The same slim bar as the loaded story, so the header doesn't swap under the visitor. */}
          <PublicOrgHeader />
          <main className="flex-1">
            <div className="mx-auto max-w-[1140px] space-y-6 px-4 py-10 sm:px-[30px]">{skeletons}</div>
          </main>
          <PublicOrgFooter />
        </div>
      </PublicSignInProvider>
    );
  }

  return (
    <PageShell>
      <main className="mx-auto max-w-[1040px] space-y-6 px-4 py-10 sm:px-[30px]">{skeletons}</main>
    </PageShell>
  );
};

const yes = (v: boolean | null | undefined) => v === true;
const has = (v: string | null | undefined): v is string => typeof v === "string" && v.trim() !== "";

/** A titled block of prose — mission, story, differentiator. Omitted when empty. */
const Prose = ({ kicker, title, text }: { kicker: string; title?: string; text: string | null }) => {
  if (!has(text)) return null;
  return (
    <section aria-label={title ?? kicker}>
      <div className="eyebrow mb-2.5 text-primary">{kicker}</div>
      {title && <h2 className="m-0 mb-3 font-display text-2xl font-semibold leading-[1.12] text-ink-900">{title}</h2>}
      <RichTextRenderer content={text} className="text-[16px] leading-[1.7] text-ink-700" />
    </section>
  );
};

/** label → value pairs, rendered as a definition list. Rows with no value are omitted. */
const Facts = ({ rows }: { rows: [string, string | null | undefined][] }) => {
  const present = rows.filter(([, v]) => has(v));
  if (!present.length) return null;
  return (
    <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
      {present.map(([label, value]) => (
        <div key={label} className="flex flex-col gap-0.5 border-b border-border pb-3">
          <dt className="text-[12.5px] font-medium uppercase tracking-[0.06em] text-ink-500">{label}</dt>
          <dd className="m-0 text-[15px] font-medium text-ink-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

const listOr = (items: string[], fallback?: string | null) => (items.length ? items.join(", ") : fallback ?? null);

const PublicOrganizationProfile = () => {
  const { slug = "" } = useParams();
  const prerender = usePrerenderData();

  const initialData = prerender.organization && prerender.organization.slug === slug ? prerender.organization : undefined;

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["public-organization", slug],
    queryFn: () => fetchPublicOrganizationProfile(slug),
    enabled: !!slug,
    initialData,
    staleTime: 5 * 60 * 1000,
  });

  const org: Profile | null | undefined = data;
  const resolvingRedirect = useRetiredSlugRedirect(slug, !isLoading && !isError && data === null);

  /**
   * One external profile-view event per successful public load (issue #663).
   *
   * An anonymous visitor here IS the recruiting signal — founder decision — and
   * until now that view was silently discarded, because the analytics tables'
   * only INSERT policy is `TO authenticated`. The row is written by the
   * `record-public-profile-view` Edge Function, which re-resolves the slug
   * server-side and owns `org_id`, `viewer_user_id` and `viewer_relationship`.
   *
   * Four things this must not do, and how each is prevented:
   *
   *   SSR / prerender  effects do not run under renderToString, and
   *                    `trackPublicProfileView` refuses without a `window`;
   *   loading          `org` is undefined until the query settles;
   *   error/not-found  `org` is null, and the effect requires a real profile
   *                    whose slug matches the route;
   *   more than once   a ref keyed by slug, so a re-render, a refetch or a
   *                    focus event cannot re-fire it, while navigating to a
   *                    different organization legitimately can.
   *
   * `FEATURE_FLAGS.RECORD_PUBLIC_PROFILE_VIEWS` is the deployment-order gate
   * and the rollback switch: a merge deploys the frontend and the Edge
   * Functions independently, so the client call is enabled only once the
   * function is verified live. See the flag's own comment.
   *
   * Declared BEFORE the early returns below so the hook order stays stable
   * across the loading and not-found states.
   */
  const trackedSlugRef = useRef<string | null>(null);
  /**
   * Derived from `org?.value.onboarding_stages` (not the post-early-return
   * `v`) and called unconditionally here so the hook order stays stable
   * across the loading and not-found states, matching the ref/effect above.
   */
  const onboardingStages = useMemo(
    () => normalizeOnboardingStages(org?.value.onboarding_stages ?? []),
    [org?.value.onboarding_stages],
  );
  // The public payload carries no catalog; tool categories are already
  // resolved, and duplicates dropped, in SQL.
  const profileData = useMemo(() => (org ? fromPublicProfile(org).profileData : {}), [org]);
  const stack = useMemo(() => buildToolStack(profileData, []), [profileData]);
  const support = useMemo(() => normalizeSupport(profileData), [profileData]);
  useEffect(() => {
    if (!FEATURE_FLAGS.RECORD_PUBLIC_PROFILE_VIEWS) return;
    if (!org || org.slug !== slug) return;
    if (trackedSlugRef.current === org.slug) return;
    trackedSlugRef.current = org.slug;
    void trackPublicProfileView(org.slug);
  }, [org, slug]);

  if (isLoading || resolvingRedirect) return <Loading bright={FEATURE_FLAGS.AGENT_UI_V2} />;
  // A failed lookup is not "not found" (issue #1063): retry, and no noindex.
  if (!org && isError) {
    return <PublicLoadError title="Organization" onRetry={() => void refetch()} retrying={isFetching} />;
  }
  if (!org) return <NotFound />;

  const title = profileTitle(org);
  const description = profileDescription(org);
  const where = locationLabel(org);
  const kind = orgTypeLabel(org.type);
  // The cloud label as the public RPC resolved it (work_model, closed enum).
  const workModel = publicWorkModelView(org.work_model);
  const officesDisplay = officesDisplayFor(workModel, org.offices_display);
  const path = organizationPath(org.slug);
  const socialImage = org.cover_url ?? org.logo_url ?? null;
  const website = httpUrl(org.website);
  const v = org.value;
  const perf = org.performance;
  const hasStory = has(org.story.mission) || has(org.story.our_story) || has(org.story.what_makes_us_different);
  const production = [
    ["Sales volume", formatCompactCurrency(perf.production.total_volume)],
    ["Transactions", formatInteger(perf.production.total_transactions)],
    ["Average sale price", formatCompactCurrency(perf.production.avg_sale_price)],
    ["Volume growth (YoY)", formatPercent(perf.production.volume_growth_yoy)],
    ["Agent retention", formatPercent(perf.agents.retention_rate)],
    ["Average tenure", has(perf.agents.avg_tenure) ? `${perf.agents.avg_tenure} years` : null],
    ["New-agent success rate", formatPercent(perf.agents.new_agent_success_rate)],
    ["Agents hitting income goal", formatPercent(perf.agents.income_goal_hit_rate)],
    ["Market share", formatPercent(perf.market_presence.market_share)],
    ["Days on market", perf.market_presence.days_on_market],
    ["List-to-sale ratio", formatPercent(perf.market_presence.list_to_sale_ratio)],
  ] as [string, string | null][];
  const hasPerformance = production.some(([, val]) => has(val)) || perf.rankings.length > 0;
  // Founder decision F6 (2026-10-21): the compensation model, fees and leads
  // need a free account. This page is only ever rendered signed-out
  // (src/App.tsx sends a signed-in visitor to the full profile), so when the
  // organization has any of them, one sign-up prompt row stands in for all.
  const gatedRows: [string, string | null][] = [
    ["Compensation model", v.primary_compensation_model],
    ["Recurring fees", v.no_recurring_fees === null ? null : v.no_recurring_fees ? "None" : listOr(feeTypeLabels(v.fee_types), "Yes")],
    ["Leads provided", v.provides_leads === null ? null : v.provides_leads ? listOr(v.lead_sources, "Yes") : "No"],
    ["Lead volume", v.lead_volume],
  ];
  const hasInsightsTeaser = gatedRows.some(([, val]) => has(val));
  const valueRows: [string, string | null][] = [
    ["Training", listOr(v.training_frequency)],
    ["One-on-one coaching", yes(v.one_on_one_coaching) ? v.coaching_frequency ?? "Yes" : null],
    ["Training library", yes(v.has_training_library) ? "Yes" : null],
    ["Health insurance", yes(v.has_health_insurance) ? "Yes" : null],
    ["Retirement plan", yes(v.has_retirement_plan) ? "Yes" : null],
    ["Partnership opportunities", yes(v.partnership_opportunities) ? listOr(v.partnership_types, "Yes") : null],
  ];
  const hasValueCard =
    hasInsightsTeaser ||
    valueRows.some(([, val]) => has(val)) ||
    v.training_programs.length > 0 ||
    v.career_stages.length > 0 ||
    v.selected_benefits.length > 0;
  const hasValue = hasValueCard || onboardingStages.length > 0;
  const articles = org.content;
  const offices = org.offices.length ? org.offices.map((o) => locationLabel(o)).filter(has) : org.office_locations;

  const head = (
    <>
      <CanonicalTags path={path} title={title} description={description} />
      <Helmet>
        <meta property="og:type" content="profile" />
        {socialImage && <meta property="og:image" content={socialImage} />}
        {socialImage && <meta name="twitter:image" content={socialImage} />}
        <script type="application/ld+json">{serializeJsonLd(organizationJsonLd(org))}</script>
      </Helmet>
    </>
  );

  const breadcrumb = (
    <nav aria-label="Breadcrumb" className="mb-3 text-[13px] text-ink-500">
      <Link to="/teams-brokerages" className="hover:text-ink-900">
        Teams &amp; Brokerages
      </Link>
      <span className="mx-2">/</span>
      <span className="text-ink-700">{org.name}</span>
    </nav>
  );

  /**
   * A basic listing (#1040): identity facts, the "Basic listing" banner and
   * section-level "Not shared yet", in the `.mk-app` shell whatever
   * AGENT_UI_V2 says — it is a new page with no legacy look to preserve. Same
   * head tags (canonical, no noindex) and JSON-LD as an owned page.
   */
  if (isListedPublicProfile(org)) {
    return (
      <PublicSignInProvider>
        <div className="mk-app flex min-h-screen flex-col">
          <PublicOrgHeader />
          {head}
          <main className="flex-1">
            <ListedOrganizationView
              org={listedIdentityFromPublic(org)}
              facts={listingFactsFromPublic(org.listing_facts)}
              topSlot={breadcrumb}
            />
          </main>
          <PublicOrgFooter />
        </div>
      </PublicSignInProvider>
    );
  }

  /**
   * The bright story page (spec 2026-09-24 §6.4). `.theme-bright` wraps the
   * body only — the marketing header and footer keep the cream palette. Head
   * tags, JSON-LD and the view beacon are shared with the old body.
   *
   * NOT `PageShell`: that wrapper has no `flex-1` on its body, so a short
   * story (little content, few sections) leaves a band of the OLD cream
   * background between the story and the footer. This structure instead
   * fills `theme-bright` all the way to the footer regardless of content
   * height.
   */
  if (FEATURE_FLAGS.AGENT_UI_V2) {
    return (
      <PublicSignInProvider>
        <div className="mk-app flex min-h-screen flex-col">
          {/* A direct link lands here: the org's page, not the marketing site. */}
          <PublicOrgHeader />
          {head}
          <main className="flex-1 pb-20">
            <PublicOrgStory
              profile={org}
              topSlot={breadcrumb}
            />
          </main>
          <PublicOrgFooter />
        </div>
      </PublicSignInProvider>
    );
  }

  return (
    <PageShell>
      {head}

      <main className="mx-auto max-w-[1040px] px-4 pb-20 pt-6 sm:px-[30px]">
        <nav aria-label="Breadcrumb" className="mb-4 text-[13px] text-ink-500">
          <Link to="/teams-brokerages" className="hover:text-ink-900">
            Teams &amp; Brokerages
          </Link>
          <span className="mx-2">/</span>
          <span className="text-ink-700">{org.name}</span>
        </nav>

        {/* Header */}
        <header>
          <SurfaceCard padding="lg" className="overflow-hidden rounded-2xl">
            {org.cover_url && (
              <img
                src={org.cover_url}
                alt=""
                className="-mx-6 -mt-6 mb-6 aspect-[3/1] w-[calc(100%+3rem)] max-w-none object-cover"
              />
            )}
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="flex min-w-0 flex-1 flex-col gap-5 sm:flex-row sm:items-start">
              {org.logo_url ? (
                <img
                  src={org.logo_url}
                  alt={`${org.name} logo`}
                  className="h-20 w-20 shrink-0 rounded-xl border border-border bg-card object-contain p-1"
                />
              ) : (
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-border bg-card">
                  <Building2 className="h-8 w-8 text-ink-500" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h1 className="m-0 font-display text-[32px] font-semibold leading-[1.1] tracking-[-0.02em] text-ink-900 sm:text-[40px]">
                  {org.name}
                </h1>
                {has(org.tagline) && <p className="mt-2 text-[17px] leading-[1.5] text-ink-700">{org.tagline}</p>}
                <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-ink-500">
                  {kind && <span>Real estate {kind.toLowerCase()}</span>}
                  <WorkModelBadge badge={workModel.badge} />
                  {where && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {where}
                    </span>
                  )}
                  {org.founded_year && <span>Est. {org.founded_year}</span>}
                  {org.agent_count && <span>{org.agent_count} agents</span>}
                  {org.is_hiring && <span className="font-medium text-primary">Hiring</span>}
                </p>
                {website && (
                  <a
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Visit website
                  </a>
                )}
              </div>
            </div>
            <LookingForCard
              className="lg:w-[360px] lg:shrink-0"
              lookingFor={lookingForFromPublic(org.looking_for)}
              offices={offices}
              officesDisplay={officesDisplay}
              orgId={org.id}
              viewer="signedOut"
              signInHref="/agents"
            />
            </div>
            {cultureTags(org.service_types).length > 0 && <TagRow tags={cultureTags(org.service_types)} className="mt-6" />}
          </SurfaceCard>
        </header>

        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-10">
            {hasStory && (
              <SurfaceCard padding="lg" className="space-y-8 rounded-2xl">
                <Prose kicker="Mission" text={org.story.mission} />
                <Prose kicker="Our story" title={`About ${org.name}`} text={org.story.our_story} />
                <Prose kicker="What makes us different" text={org.story.what_makes_us_different} />
                {has(org.story.quote_text) && (
                  <blockquote className="m-0 border-l-2 border-primary pl-5">
                    <p className="m-0 font-display text-[22px] leading-[1.35] text-ink-900">“{org.story.quote_text}”</p>
                    {has(org.story.quote_attribution) && (
                      <footer className="mt-2 text-[13.5px] text-ink-500">— {org.story.quote_attribution}</footer>
                    )}
                  </blockquote>
                )}
              </SurfaceCard>
            )}

            {!hasStory && has(org.description) && (
              <SurfaceCard padding="lg" className="rounded-2xl">
                <Prose kicker="About" text={org.description} />
              </SurfaceCard>
            )}

            {onboardingStages.length > 0 && (
              <OnboardingPlanSummary
                stages={onboardingStages}
                action={
                  <a href={`#${ONBOARDING_PLAN_ANCHOR_ID}`} className="text-sm font-medium text-primary hover:underline">
                    View the full plan
                  </a>
                }
              />
            )}

            {org.by_the_numbers.length > 0 && (
              <section aria-labelledby="by-the-numbers">
                <SectionHead kicker="At a glance" title="By the numbers" />
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {org.by_the_numbers.map((s) => (
                    <SurfaceCard key={`${s.label}-${s.value}`} className="rounded-xl">
                      <div className="font-display text-[28px] font-semibold leading-none text-ink-900">{s.value}</div>
                      <div className="mt-2 text-[13.5px] font-medium text-ink-700">{s.label}</div>
                      {has(s.context) && <div className="mt-0.5 text-[12.5px] text-ink-500">{s.context}</div>}
                    </SurfaceCard>
                  ))}
                </div>
                <span id="by-the-numbers" className="sr-only">
                  By the numbers
                </span>
              </section>
            )}

            {hasValue && (
              <section aria-labelledby="value-proposition">
                <SectionHead kicker="Working here" title="What agents get" />
                {hasValueCard && (
                  <SurfaceCard padding="lg" className="space-y-6 rounded-2xl">
                    {hasInsightsTeaser && (
                      <SignUpPrompt unlock="compensation" label={GATED_INSIGHTS_LABEL} />
                    )}
                    <Facts rows={valueRows} />
                    {v.training_programs.length > 0 && (
                      <div>
                        <h3 className="mb-2 text-[13px] font-medium uppercase tracking-[0.06em] text-ink-500">Training programs</h3>
                        <TagRow tags={v.training_programs} />
                      </div>
                    )}
                    {v.career_stages.length > 0 && (
                      <div>
                        <h3 className="mb-2 text-[13px] font-medium uppercase tracking-[0.06em] text-ink-500">Career path</h3>
                        <ol className="m-0 flex flex-wrap gap-2 p-0 text-[14px] text-ink-700">
                          {v.career_stages.map((stage, i) => (
                            <li key={stage} className="list-none">
                              {i > 0 && <span className="mr-2 text-ink-500">→</span>}
                              {stage}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {v.selected_benefits.length > 0 && (
                      <div>
                        <h3 className="mb-2 text-[13px] font-medium uppercase tracking-[0.06em] text-ink-500">Benefits &amp; perks</h3>
                        <TagRow tags={v.selected_benefits} />
                      </div>
                    )}
                  </SurfaceCard>
                )}
                {onboardingStages.length > 0 && (
                  <div className={hasValueCard ? "mt-8" : undefined}>
                    <OnboardingPlan stages={onboardingStages} />
                  </div>
                )}
                <span id="value-proposition" className="sr-only">
                  What agents get
                </span>
              </section>
            )}

            {hasPerformance && (
              <section aria-labelledby="performance">
                <SectionHead
                  kicker="Performance"
                  title={perf.reporting_year ? `Production, ${perf.reporting_year}` : "Production"}
                  aside={<span className="text-[12.5px] text-ink-500">Self-reported by the organization</span>}
                />
                <SurfaceCard padding="lg" className="space-y-6 rounded-2xl">
                  <Facts rows={production} />
                  {perf.rankings.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-[13px] font-medium uppercase tracking-[0.06em] text-ink-500">Rankings</h3>
                      <ul className="m-0 space-y-1.5 p-0 text-[14.5px] text-ink-700">
                        {perf.rankings.map((r, i) => (
                          <li key={i} className="list-none">
                            {has(r.rank) && <span className="font-semibold text-ink-900">{r.rank} </span>}
                            {r.category}
                            {has(r.region) && <span className="text-ink-500"> · {r.region}</span>}
                            {has(r.source) && <span className="text-ink-500"> · {r.source}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {perf.market_presence.specializations.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-[13px] font-medium uppercase tracking-[0.06em] text-ink-500">Specializations</h3>
                      <TagRow tags={perf.market_presence.specializations} />
                    </div>
                  )}
                </SurfaceCard>
                <span id="performance" className="sr-only">
                  Performance
                </span>
              </section>
            )}

            {articles.length > 0 && (
              <section aria-labelledby="newsroom">
                <SectionHead kicker="Newsroom" title={`News from ${org.name}`} />
                <ul className="m-0 grid gap-4 p-0 sm:grid-cols-2">
                  {articles.map((a) => {
                    const date = articleDate(a);
                    const summary = articleExcerpt(a);
                    const readTime = articleReadTime(a);
                    return (
                      <li key={a.id} className="list-none">
                        <article className="h-full">
                          <SurfaceCard className="flex h-full flex-col overflow-hidden rounded-xl" padding="md">
                            {a.image_url && (
                              <img
                                src={a.image_url}
                                alt=""
                                loading="lazy"
                                className="-mx-5 -mt-5 mb-4 aspect-[16/9] w-[calc(100%+2.5rem)] max-w-none object-cover"
                              />
                            )}
                            <div className="eyebrow mb-2 text-primary">{contentTypeLabel(a.type)}</div>
                            <h3 className="m-0 font-display text-[19px] font-semibold leading-[1.25] text-ink-900">
                              <Link to={organizationArticlePath(org.slug, a.id)} className="hover:underline">
                                {a.title}
                              </Link>
                            </h3>
                            {summary && <p className="mt-2 text-[14px] leading-[1.6] text-ink-700">{summary}</p>}
                            <p className="mt-auto pt-3 text-[12.5px] text-ink-500">
                              {date && <time dateTime={date}>{formatLongDate(date)}</time>}
                              {date && readTime && " · "}
                              {readTime}
                            </p>
                          </SurfaceCard>
                        </article>
                      </li>
                    );
                  })}
                </ul>
                <span id="newsroom" className="sr-only">
                  Newsroom
                </span>
              </section>
            )}
          </div>

          <aside className="space-y-6">
            <SurfaceCard padding="lg" className="rounded-2xl">
              <div className="eyebrow mb-2 text-primary">On Compaire</div>
              <h2 className="m-0 font-display text-[20px] font-semibold leading-[1.2] text-ink-900">
                Compare {org.name} with other teams
              </h2>
              <p className="mt-2 text-[14px] leading-[1.6] text-ink-700">
                Sign in to read agent reviews, see the fit score, compare side by side, and message the team privately.
              </p>
              <div className="mt-4 space-y-3">
                <SignUpPrompt unlock="compare" className="sm:flex-col sm:items-start" />
                <SignUpPrompt unlock="message" className="sm:flex-col sm:items-start" />
              </div>
            </SurfaceCard>

            <OfficesCard offices={offices} display={officesDisplay} cloud={workModel.treatedAsCloud} />

            {(!stack.isEmpty || !support.isEmpty) && (
              <SurfaceCard padding="lg" className="rounded-2xl">
                <h2 className="m-0 mb-3 text-[13px] font-medium uppercase tracking-[0.06em] text-ink-500">How it runs</h2>
                <OperationsSpecSheet stack={stack} support={support} />
              </SurfaceCard>
            )}

            {org.awards.length > 0 && (
              <SurfaceCard padding="lg" className="rounded-2xl">
                <h2 className="m-0 mb-3 text-[13px] font-medium uppercase tracking-[0.06em] text-ink-500">Awards &amp; recognition</h2>
                <ul className="m-0 space-y-3 p-0">
                  {org.awards.map((a) => (
                    <li key={a.id} className="flex list-none items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold-tint">
                        <Trophy className="h-4 w-4 text-featured" strokeWidth={1.8} aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-semibold leading-[1.3] text-ink-900">{a.name}</span>
                        <span className="block text-[12.5px] text-ink-500">
                          {a.org} · {a.year}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </SurfaceCard>
            )}
          </aside>
        </div>
      </main>
    </PageShell>
  );
};

export default PublicOrganizationProfile;
